import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Modal, Overlay } from 'react-bootstrap';
import PlaceholderImage from 'assets/img/template-placeholder.jpg';
import akarFile from 'assets/img/akar-file.svg';
import dotsThreeIcon from 'assets/img/DotsThree.svg';
import editIcon from 'assets/img/edit-pencil.svg';
import deleteIcon from 'assets/img/delete3.svg';
import { selectLoader, hasPermission } from 'utils/Helpers';
import { useAuth } from 'contexts/AuthContext';
import APIServices from 'services/APIServices';
import appToast, { TOAST_TYPE } from 'utils/AppToast';
import DeleteModal from 'components/DeleteModal';
import './SavedTemplates.css';

const TemplateActions = ({ template, isOpen, onToggle, onEdit, onDelete }) => {
  const targetRef = useRef(null);

  return (
    <div className="saved-template-actions" onClick={(event) => event.stopPropagation()}>
      <button
        ref={targetRef}
        type="button"
        className="saved-template-menu-button"
        aria-label={`More actions for ${template.name}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => onToggle(isOpen ? null : template._id)}>
        <img src={dotsThreeIcon} alt="" aria-hidden="true" />
      </button>

      <Overlay
        show={isOpen}
        target={targetRef.current}
        placement="bottom-end"
        container={document.body}
        rootClose
        flip
        onHide={() => onToggle(null)}>
        {({ ...overlayProps }) => (
          <div
            {...overlayProps}
            className="saved-template-menu"
            role="menu"
            onClick={(event) => event.stopPropagation()}
            style={{ ...overlayProps.style, position: 'fixed', zIndex: 2000 }}>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onToggle(null);
                onEdit(template);
              }}>
              <img src={editIcon} alt="" aria-hidden="true" />
              Edit name
            </button>
            <button
              type="button"
              role="menuitem"
              className="saved-template-delete-action"
              onClick={() => {
                onToggle(null);
                onDelete(template);
              }}>
              <img src={deleteIcon} alt="" aria-hidden="true" />
              Delete
            </button>
          </div>
        )}
      </Overlay>
    </div>
  );
};

const SavedTemplates = ({
  fetchTemplates,
  tempates,
  handleTemplateClick,
  showArchivedList,
  archivedTempates,
  handleProjectClick
}) => {
  const { userRole } = useAuth();
  const canManageTemplates = hasPermission(userRole, 'update_projects');
  const listRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(!showArchivedList);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [templateNameOverrides, setTemplateNameOverrides] = useState({});
  const [hiddenTemplateIds, setHiddenTemplateIds] = useState(() => new Set());

  const sourceTemplates = showArchivedList ? archivedTempates : tempates;
  const templatesToShow = sourceTemplates
    .filter((template) => !hiddenTemplateIds.has(template._id))
    .map((template) =>
      templateNameOverrides[template._id] ? { ...template, name: templateNameOverrides[template._id] } : template
    );

  useEffect(() => {
    if (showArchivedList) return;

    const loadInitial = async () => {
      setInitialLoading(true);
      try {
        const more = await fetchTemplates(1);
        setHasMore(more);
        setPage(1);
      } catch (error) {
        console.error('Error loading templates:', error);
        appToast('Failed to load templates. Please try again.', TOAST_TYPE.ERROR);
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitial();
  }, [fetchTemplates, showArchivedList]);

  const handleScroll = useCallback(async () => {
    if (showArchivedList || initialLoading || !listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setLoading(true);
      const nextPage = page + 1;
      try {
        const more = await fetchTemplates(nextPage);
        setHasMore(more);
        setPage(nextPage);
      } catch (error) {
        console.error('Error loading more templates:', error);
        appToast('Failed to load more templates.', TOAST_TYPE.ERROR);
      } finally {
        setLoading(false);
      }
    }
  }, [initialLoading, loading, hasMore, page, fetchTemplates, showArchivedList]);

  useEffect(() => {
    const container = listRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name || '');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedTemplate(null);
    setTemplateName('');
  };

  const handleUpdateTemplate = async (event) => {
    event.preventDefault();
    const name = templateName.trim();
    const templateId = selectedTemplate?._id;

    if (!name || !templateId) {
      appToast('Template name is required.', TOAST_TYPE.ERROR);
      return;
    }

    setTemplateNameOverrides((current) => ({ ...current, [templateId]: name }));
    setShowEditModal(false);
    setSelectedTemplate(null);
    setTemplateName('');

    try {
      const response = await APIServices.put(`/project-board-templates/${templateId}`, { name });

      if (response?.status >= 200 && response?.status < 300) {
        appToast(response.message || 'Template name updated successfully.', TOAST_TYPE.SUCCESS);
        await fetchTemplates(1);
        setTemplateNameOverrides((current) => {
          const next = { ...current };
          delete next[templateId];
          return next;
        });
      } else {
        setTemplateNameOverrides((current) => {
          const next = { ...current };
          delete next[templateId];
          return next;
        });
        appToast(response?.data?.error || response?.data?.message || 'Failed to update template.', TOAST_TYPE.ERROR);
      }
    } catch (error) {
      setTemplateNameOverrides((current) => {
        const next = { ...current };
        delete next[templateId];
        return next;
      });
      appToast(error?.response?.data?.message || 'Failed to update template.', TOAST_TYPE.ERROR);
    }
  };

  const openDeleteModal = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = async () => {
    const templateId = selectedTemplate?._id;
    if (!templateId) return;

    setHiddenTemplateIds((current) => new Set(current).add(templateId));
    setShowDeleteModal(false);
    setSelectedTemplate(null);

    try {
      const response = await APIServices.put(`/project-board-templates/delete/${templateId}`);

      if (response?.status >= 200 && response?.status < 300) {
        appToast(response.message || 'Template deleted successfully.', TOAST_TYPE.SUCCESS);
        await fetchTemplates(1);
        setHiddenTemplateIds((current) => {
          const next = new Set(current);
          next.delete(templateId);
          return next;
        });
      } else {
        setHiddenTemplateIds((current) => {
          const next = new Set(current);
          next.delete(templateId);
          return next;
        });
        appToast(response?.data?.error || response?.data?.message || 'Failed to delete template.', TOAST_TYPE.ERROR);
      }
    } catch (error) {
      setHiddenTemplateIds((current) => {
        const next = new Set(current);
        next.delete(templateId);
        return next;
      });
      appToast(error?.response?.data?.message || 'Failed to delete template.', TOAST_TYPE.ERROR);
    }
  };

  return (
    <div>
      <div className="saveTemplete-container" ref={listRef}>
        <div className="row row-px">
          {!showArchivedList && initialLoading ? (
            <div className="saved-templates-initial-loader">{selectLoader(45)}</div>
          ) : templatesToShow.length > 0 ? (
            templatesToShow.map((template) => (
              <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12" key={template._id}>
                <div
                  className="cardBg-gray cursor_pointer"
                  onClick={() => {
                    if (showArchivedList) {
                      handleProjectClick(template._id, true);
                    } else {
                      handleTemplateClick(template._id);
                    }
                  }}>
                  <img
                    src={
                      showArchivedList
                        ? template.boardImage || PlaceholderImage
                        : template.templateImage || PlaceholderImage
                    }
                    alt={template.name}
                    className="savedTemplateImg-img"
                  />
                  <p title={template.name}>{template.name}</p>
                  {!showArchivedList && canManageTemplates && (
                    <TemplateActions
                      template={template}
                      isOpen={openMenuId === template._id}
                      onToggle={setOpenMenuId}
                      onEdit={openEditModal}
                      onDelete={openDeleteModal}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <section className="blank-sec dashboard-sec">
              <div className="container">
                <div className="row align-items-start">
                  <div className="col-lg-12">
                    <div className="blankList-box-cover">
                      <div className="blankList-box">
                        <img className="akarFile" src={akarFile} alt="" />
                        <h2>No templates available</h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {!showArchivedList && !initialLoading && loading && templatesToShow.length > 0 && (
          <div className="text-center py-3 w-100">{selectLoader(35)}</div>
        )}
      </div>

      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        centered
        className="deletModal create-board-modal saved-template-edit-modal">
        <form onSubmit={handleUpdateTemplate}>
          <Modal.Header closeButton>
            <div className="modal-title">Edit Template</div>
          </Modal.Header>
          <Modal.Body className="save-templete-cover">
            <div className="save-templete-box">
              <label htmlFor="saved-template-name">Template name*</label>
              <input
                id="saved-template-name"
                type="text"
                className="form-control"
                value={templateName}
                maxLength={80}
                autoFocus
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder="Write template name"
              />
            </div>
            <div className="save-templete-group-btn">
              <button
                type="submit"
                className={`black-btn cursor_pointer ${!templateName.trim() ? 'disabled-btn' : ''}`}
                disabled={!templateName.trim()}>
                Save
              </button>
            </div>
          </Modal.Body>
        </form>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        handleDelete={handleDeleteTemplate}
        loading={false}
        itemName="template"
      />
    </div>
  );
};

export default SavedTemplates;
